import { useRef, useState } from 'react';
import { Bug, Send, Loader2, ImagePlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/ui/searchable-select';
import axiosInstance from '@/lib/api/axios-instance';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils/cn';
import { EnLaRanuraDeAyuda } from './ranura-de-la-barra';

const MAX_IMAGES = 4;
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const BUG_TYPES = [
  { value: 'bug', label: '🐛 Bug — Algo no funciona como debería' },
  { value: 'crash', label: '💥 Crash — La aplicación se rompe o falla' },
  { value: 'ui', label: '🎨 Error visual — Algo se ve mal en la interfaz' },
  { value: 'data', label: '📊 Error de datos — Datos incorrectos o faltantes' },
  { value: 'suggestion', label: '💡 Sugerencia — Idea de mejora' },
];

interface BugReportForm {
  title: string;
  type: string;
  description: string;
  steps: string;
  expected: string;
  actual: string;
}

const EMPTY_FORM: BugReportForm = {
  title: '',
  type: '',
  description: '',
  steps: '',
  expected: '',
  actual: '',
};

interface ImageAttachment {
  file: File;
  previewUrl: string;
}

export function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<BugReportForm>(EMPTY_FORM);
  const [images, setImages] = useState<ImageAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOpen = () => {
    setForm(EMPTY_FORM);
    setImages((prev) => {
      prev.forEach((i) => URL.revokeObjectURL(i.previewUrl));
      return [];
    });
    setSent(false);
    setError('');
    setOpen(true);
  };

  const handleChange = (field: keyof BugReportForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAddImages = (filesList: FileList | null) => {
    if (!filesList) return;
    const incoming = Array.from(filesList);
    const tooBig = incoming.find((f) => f.size > MAX_SIZE_BYTES);
    if (tooBig) {
      setError(`${tooBig.name} supera los ${MAX_SIZE_MB} MB`);
      return;
    }
    const notImage = incoming.find((f) => !f.type.startsWith('image/'));
    if (notImage) {
      setError(`${notImage.name} no es una imagen`);
      return;
    }
    setImages((prev) => {
      const space = MAX_IMAGES - prev.length;
      if (space <= 0) {
        setError(`Máximo ${MAX_IMAGES} imágenes`);
        return prev;
      }
      const accepted = incoming.slice(0, space).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      if (incoming.length > space) {
        setError(`Solo se agregaron ${space} (máx. ${MAX_IMAGES})`);
      } else {
        setError('');
      }
      return [...prev, ...accepted];
    });
  };

  const removeImage = (idx: number) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError('El título es obligatorio');
      return;
    }
    if (!form.type) {
      setError('Selecciona el tipo de problema');
      return;
    }
    if (!form.description.trim() || form.description.trim().length < 20) {
      setError('La descripción debe tener al menos 20 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('type', form.type);
      fd.append('description', form.description);
      if (form.steps) fd.append('steps', form.steps);
      if (form.expected) fd.append('expected', form.expected);
      if (form.actual) fd.append('actual', form.actual);
      fd.append('url', window.location.href);
      fd.append(
        'browser',
        `${navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/)?.[0] ?? 'Desconocido'} — ${navigator.platform}`
      );
      images.forEach((img) => fd.append('images', img.file, img.file.name));

      await axiosInstance.post(ENDPOINTS.BUG_REPORT, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Error al enviar el reporte. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* En la barra superior (al lado del tema); flotante sólo si no hay barra. */}
      <EnLaRanuraDeAyuda
        enLaBarra={
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={handleOpen}
            title="Reportar un problema"
            aria-label="Reportar un problema"
          >
            <Bug className="h-5 w-5" />
          </Button>
        }
        sinBarra={
          <button
            onClick={handleOpen}
            title="Reportar un problema"
            className={cn(
              'fixed bottom-[88px] right-6 z-40',
              'w-10 h-10 rounded-full shadow-lg',
              'bg-destructive/90 hover:bg-destructive text-destructive-foreground',
              'flex items-center justify-center',
              /*
                Superficie chica y redonda: acá sí se hunde (plan 175 · B2 y B3).

                El `active` no baja de 1: baja del 1,10 del hover. Un botón que crece
                al pasarle el mouse y vuelve a 1,04 al apretarlo se siente empujado;
                si el active fuera `.97` daría un salto de 13 puntos que se lee como
                un parpadeo, no como una presión.
              */
              'transition-[background-color,transform] duration-snap ease-snap',
              'hover:scale-110 active:scale-[1.04]',
              'focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2'
            )}
          >
            <Bug className="w-4 h-4" />
          </button>
        }
      />

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          aria-describedby={undefined}
          className="max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Bug className="w-4 h-4 text-destructive" />
              Reportar problema
            </DialogTitle>
          </DialogHeader>

          {sent ? (
            <div className="py-8 text-center space-y-3">
              <div className="text-4xl">✅</div>
              <p className="font-medium text-sm">¡Reporte enviado!</p>
              <p className="text-xs text-muted-foreground">
                Tu reporte fue enviado correctamente. Lo revisaremos pronto.
              </p>
              <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
                Cerrar
              </Button>
            </div>
          ) : (
            <div className="space-y-4 pt-1">
              {/* Tipo */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Tipo de problema <span className="text-destructive">*</span>
                </Label>
                <SearchableSelect
                  options={BUG_TYPES}
                  value={form.type}
                  onValueChange={(v) => handleChange('type', v)}
                  placeholder="Selecciona el tipo..."
                  searchPlaceholder="Buscar tipo..."
                />
              </div>

              {/* Título */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Título breve <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="Ej: El botón de guardar cotización no responde"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="text-sm"
                />
              </div>

              {/* Descripción */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Descripción detallada <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  placeholder="Describe con el mayor detalle posible qué está pasando, en qué circunstancias ocurre, desde cuándo, etc."
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={4}
                  className="text-sm resize-none"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {form.description.length} caracteres {form.description.length < 20 && '(mín. 20)'}
                </p>
              </div>

              {/* Pasos para reproducir */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Pasos para reproducir{' '}
                  <span className="text-muted-foreground font-normal">(opcional)</span>
                </Label>
                <Textarea
                  placeholder={`1. Ir a Cotizaciones\n2. Hacer clic en "Nueva Cotización"\n3. Completar el formulario y presionar Guardar\n4. El botón no hace nada`}
                  value={form.steps}
                  onChange={(e) => handleChange('steps', e.target.value)}
                  rows={3}
                  className="text-sm resize-none font-mono"
                />
              </div>

              {/* Esperado vs Actual */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-green-700 dark:text-green-400">
                    ✅ Qué debería pasar
                  </Label>
                  <Textarea
                    placeholder="La cotización se guarda y redirige al listado"
                    value={form.expected}
                    onChange={(e) => handleChange('expected', e.target.value)}
                    rows={2}
                    className="text-sm resize-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-destructive">
                    ❌ Qué está pasando
                  </Label>
                  <Textarea
                    placeholder="No pasa nada, la página se queda cargando"
                    value={form.actual}
                    onChange={(e) => handleChange('actual', e.target.value)}
                    rows={2}
                    className="text-sm resize-none"
                  />
                </div>
              </div>

              {/* Adjuntar imágenes */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Imágenes / capturas{' '}
                  <span className="text-muted-foreground font-normal">
                    (opcional, máx. {MAX_IMAGES} · {MAX_SIZE_MB}MB c/u)
                  </span>
                </Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handleAddImages(e.target.files);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                />
                {images.length === 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full"
                  >
                    <ImagePlus className="w-3.5 h-3.5 mr-1.5" />
                    Agregar imágenes
                  </Button>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {images.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative group rounded-md overflow-hidden border bg-muted/30 aspect-square"
                      >
                        <img
                          src={img.previewUrl}
                          alt={`Adjunto ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          aria-label="Quitar imagen"
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-[opacity,transform] duration-snap ease-snap active:scale-90"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {images.length < MAX_IMAGES && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square rounded-md border-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/60 hover:bg-muted/30 flex items-center justify-center transition-[border-color,background-color,transform] duration-snap ease-snap active:scale-[.97] text-muted-foreground"
                      >
                        <ImagePlus className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Info auto-capturada */}
              <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground space-y-0.5">
                <p className="font-medium text-foreground/60">Se enviará automáticamente:</p>
                <p>
                  🌐 URL: <code className="text-xs">{window.location.pathname}</code>
                </p>
                <p>
                  💻 Navegador:{' '}
                  {navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/)?.[0] ?? 'N/D'}
                </p>
              </div>

              {error && (
                <p className="text-xs text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                  {error}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-3 h-3 mr-1.5" />
                      Enviar reporte
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
